import type { CartItem } from "@pufimi/shared";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";

// The server cart quote as a react-query query (ADR-0054 §2/§3): re-priced +
// ownership-re-verified lines + the money breakdown for the current cart. Keyed on the
// exact `{ productId, quantity }` set so a stepper change re-quotes; `keepPreviousData`
// holds the last lines visible during the refetch so the tree never flickers to empty.
// The delivery method is NOT in the key — both flat rates come back on `shipping`, and
// the checkout page recomputes shipping + total live as the buyer toggles method
// (mirroring the single-line quote), so a method switch needs no round-trip. Disabled
// for an empty cart (no request fired).
//
// A stale/foreign line no longer errors: the quote is partial-tolerant and reports those
// ids for `useCartQuote` to prune. So the ONLY error left here is a genuine failure to
// reach a working API — which `retry` treats as transient (see below), and the checkout
// page surfaces as a load-error panel with a manual Retry once the retries give up.
export function cartQuoteQuery(items: CartItem[]) {
  return queryOptions({
    queryKey: ["cart-quote", items] as const,
    queryFn: () => api.cart.quote(items),
    enabled: items.length > 0,
    placeholderData: keepPreviousData,
    // A dropped connection or a 5xx is transient — ride it out with two backed-off
    // retries so a network blip doesn't read as a broken cart. A 4xx is deterministic
    // (our own request is malformed): retrying it can only fail again, so don't.
    retry: (failureCount, error) => {
      const status = error instanceof ApiError ? error.status : null;
      if (status !== null && status < 500) return false;
      return failureCount < 2;
    },
    throwOnError: false,
    staleTime: 15_000,
  });
}
